// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@stoneage/MobiusOverrides/Button/Button";
import DynamicDropdown, { OptionObject } from "@stoneage/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@stoneage/MobiusOverrides/GridContainer/GridContainer";
import { LOCATION_FILTER_ALL_PREFIX } from "@stoneage/Overrides/Widgets/LocationFinder/LocationFinder.types";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    locations?: string[];
    cancelAction: () => void;
    applyFilterAction: ({
        category,
        country,
        state,
    }: {
        category?: string;
        country?: CountryModel;
        state?: StateModel;
    }) => void;
    country: string;
    state: string;
    category: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const countryDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        countries: countryDataView.value,
        isLoading: countryDataView.isLoading,
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FilterLocationsModalStyles {
    container?: InjectableCss;
    formGrid?: GridContainerProps;
    locationTypeGrid?: GridItemProps;
    countryStateGrid?: GridItemProps;
    actionsContainer?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    loadingWrapper?: InjectableCss;
}

export const filterLocationStyles: FilterLocationsModalStyles = {
    formGrid: {
        gap: 12,
    },
    locationTypeGrid: {
        width: 12,
    },
    actionsContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 10px 16px;
            width: 100%;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
    loadingWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
};

const styles = filterLocationStyles;

const ALL = LOCATION_FILTER_ALL_PREFIX;

const toOption = (value: string): OptionObject => ({ optionText: value, optionValue: value });

const generateCountryStateStyle = (isState: boolean): GridItemProps => ({
    width: isState ? [12, 12, 12, 6, 6] : 12,
});

const FilterLocationsModal = ({
    locations,
    countries,
    loadCurrentCountries,
    applyFilterAction,
    cancelAction,
    category,
    country,
    state,
    isLoading,
}: Props) => {
    const [selectedCategoryValue, setSelectedCategoryValue] = React.useState(category || ALL);
    const [selectedCountryValue, setSelectedCountryValue] = React.useState(country || ALL);
    const [selectedStateValue, setSelectedStateValue] = React.useState(state || ALL);

    React.useEffect(() => {
        if (!isLoading && (!countries || countries.length === 0)) {
            loadCurrentCountries();
        }
    }, []);

    // XNG-Changes: re-seed from the active filters whenever the modal is reopened
    React.useEffect(() => {
        setSelectedCategoryValue(category || ALL);
        setSelectedCountryValue(country || ALL);
        setSelectedStateValue(state || ALL);
    }, [category, country, state]);

    const selectedCountry = countries?.find(item => item.abbreviation === selectedCountryValue);
    const states = selectedCountry?.states || [];
    const selectedState = states.find(item => item.abbreviation === selectedStateValue);

    const handleCountryChange = (value?: string) => {
        setSelectedCountryValue(value || ALL);
        // XNG-Changes: a state from the previous country is meaningless
        setSelectedStateValue(ALL);
    };

    const handleApplyFilterAction = () => {
        applyFilterAction({
            category: selectedCategoryValue === ALL ? "" : selectedCategoryValue,
            country: selectedCountryValue === ALL ? undefined : selectedCountry,
            state: selectedStateValue === ALL ? undefined : selectedState,
        });
        cancelAction();
    };

    const locationTypeOptions = [
        { optionText: translate("All Types"), optionValue: ALL },
        ...(locations?.map(toOption) || []),
    ];

    const countryOptions = [
        { optionText: translate("All Countries"), optionValue: ALL },
        ...(countries?.map(item => ({ optionText: item.name, optionValue: item.abbreviation })) || []),
    ];

    const stateOptions = [
        { optionText: translate("All States"), optionValue: ALL },
        ...states.map(item => ({ optionText: item.name, optionValue: item.abbreviation })),
    ];

    const hasStates = states.length > 1;
    const countryStateStyle = generateCountryStateStyle(hasStates);

    return (
        <StyledWrapper {...styles.container} data-test-selector="FilterLocationsModal">
            {isLoading && (
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            )}
            {!isLoading && (
                <GridContainer {...styles.formGrid}>
                    <GridItem {...styles.locationTypeGrid}>
                        <DynamicDropdown
                            label={translate("Location Type")}
                            options={locationTypeOptions}
                            onSelectionChange={value => setSelectedCategoryValue(value || ALL)}
                            selected={selectedCategoryValue}
                        />
                    </GridItem>
                    {countries && countries.length > 1 && (
                        <GridItem {...countryStateStyle}>
                            <DynamicDropdown
                                label={translate("Country")}
                                options={countryOptions}
                                onSelectionChange={handleCountryChange}
                                selected={selectedCountryValue}
                            />
                        </GridItem>
                    )}
                    {hasStates && (
                        <GridItem {...countryStateStyle}>
                            <DynamicDropdown
                                label={translate("State/Region")}
                                options={stateOptions}
                                onSelectionChange={value => setSelectedStateValue(value || ALL)}
                                selected={selectedStateValue}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            )}
            <StyledWrapper {...styles.actionsContainer}>
                <Button {...styles.cancelButton} onClick={cancelAction}>
                    {translate("Cancel")}
                </Button>
                <Button {...styles.submitButton} disabled={isLoading} onClick={handleApplyFilterAction}>
                    {translate("Apply Filters")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterLocationsModal);
